import { Component, OnInit } from '@angular/core';
import { ToastService } from '../../../services/toast.service';
import { EnrollmentService } from '../../../services/enrollment.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '../../../services/loading.service';
import { PortfolioMovementService, PortfolioMovementResponse, PortfolioMovementType } from '../../../services/portfolio-movement.service';
import { ExchangeRateService } from '../../../services/exchange-rate.service';
import { RefundVouchersService } from '../../../services/refund-vouchers.service';
import { AuthorizationService } from '../../../services/authorization.service';
import { ConfirmationConfig } from '../../../components/shared/confirmation-modal/confirmation-modal.component';
import { WompiWebhookService } from '../../../services/wompi-webhook.service';
import { WompiWebhookRequest, WompiWebhookRequestDetail, WompiRequestStatus, ProcessingLogEntry } from '../../../interfaces/wompi-webhook.interfaces';
import { EmailLogService, EmailLogResponseDTO } from '../../../services/email-log.service';
import { AuthService } from '../../../services/auth.service';
import { PaymentPlanAdminService } from '../../../services/payment-plan-admin.service';
import { IncomeReceiptsService } from '../../../services/income-receipts.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-view-enrollment-v2',
  templateUrl: './view-enrollment-v2.component.html',
  styleUrl: './view-enrollment-v2.component.scss'
})
export class ViewEnrollmentV2Component implements OnInit {

  enrollment!: any;
  generalIncomeReceipts: any[] = []; // 🆕 Ingresos generales (sin plan de pago)
  movementHistory: PortfolioMovementResponse[] = []; // 🆕 Historial de movimientos de cartera
  refundVouchers: any[] = []; // 🆕 Devoluciones relacionadas con la inscripción
  today = new Date();

  // 🆕 Última cotización vigente para planes pendientes
  currentExchangeRate: number | null = null;

  // 🆕 Modal de detalle de movimiento
  selectedMovement: PortfolioMovementResponse | null = null;
  showMovementModal: boolean = false;

  // 🆕 Control de tabs para cada plan de pago
  planTabs: { [planId: number]: string } = {};

  // Opciones y valor seleccionado para el dropdown de estado
  enrollmentStatusOptions: Array<{ value: number; label: string }> = [
    { value: 0, label: 'Pendiente' },
    { value: 1, label: 'Activa' },
    { value: 2, label: 'Retirada' },
    { value: 3, label: 'Aplazada' },
    { value: 4, label: 'Completada' },
    { value: 5, label: 'Refinanciada' },
    { value: 6, label: 'Reversada' },
    { value: 7, label: 'Reintegro' },
    { value: 8, label: 'Cesión de Cupo' }
  ];
  selectedEnrollmentStatus: number = 1; // Por defecto Activa

  // Modal de confirmación para cambio de estado
  showStatusConfirmationModal: boolean = false;
  statusConfirmationConfig: ConfirmationConfig = {
    title: 'Confirmar cambio de estado',
    message: '',
    type: 'warning',
    confirmText: 'Sí, cambiar estado',
    cancelText: 'Cancelar'
  };
  pendingStatusChange: { newStatus: number; statusLabel: string } | null = null;
  showReasonInput: boolean = false;
  statusChangeReason: string = '';

  // 🆕 Modal de anulación
  showCancellationModal: boolean = false;
  cancellationReason: string = '';
  isCancelling: boolean = false;

  // 🆕 Modal de confirmación para toggle de cobro automático
  showAutoPaymentConfirmationModal: boolean = false;
  pendingAutoPaymentValue: boolean = false;
  autoPaymentConfirmationConfig: ConfirmationConfig = {
    title: '',
    message: '',
    type: 'warning',
    confirmText: '',
    cancelText: 'Cancelar',
    showIcon: true
  };

  // Email logs por plan de pago
  emailsByPlan: { [planId: number]: EmailLogResponseDTO[] } = {};
  emailsLoadingByPlan: { [planId: number]: boolean } = {};

  // Webhooks Wompi por plan de pago
  webhooksByPlan: { [planId: number]: WompiWebhookRequest[] } = {};
  webhooksLoadingByPlan: { [planId: number]: boolean } = {};
  webhookDetail: { [webhookId: number]: WompiWebhookRequestDetail } = {};
  webhookDetailLoading: { [webhookId: number]: boolean } = {};
  expandedWebhooks: { [webhookId: number]: boolean } = {};
  showRawWebhookModal: boolean = false;
  rawWebhookData: string = '';
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private enrollmentService: EnrollmentService,
    private portfolioMovementService: PortfolioMovementService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private exchangeRateService: ExchangeRateService,
    private refundVouchersService: RefundVouchersService,
    private authorizationService: AuthorizationService,
    private wompiWebhookService: WompiWebhookService,
    public emailLogService: EmailLogService,
    private authService: AuthService,
    private adminService: PaymentPlanAdminService,
    private incomeReceiptsService: IncomeReceiptsService
  ) {

  }

  // 🆕 ====== Estado del modal de anulación de comprobante ======
  cancelReceiptModal: {
    open: boolean;
    receipt: any | null;
    reason: string;
    submitting: boolean;
  } = { open: false, receipt: null, reason: '', submitting: false };

  // 🔐 ====== Operaciones admin sobre cuotas ======
  // Habilitadas por el permiso ENROLLMENTS_PAYMENT_PLAN_ADMIN_OPS (espejo del backend).
  get isSuperAdmin(): boolean {
    return this.authorizationService.hasPermission('ENROLLMENTS_PAYMENT_PLAN_ADMIN_OPS');
  }

  // 🆕 Banner "Necesita atención": dispara cuando la suma de cuotas activas
  // no cuadra con TotalAmount de la inscripción. Mismo criterio que el listado.
  // Tolerancia 0.01 (centavos) para evitar falsos positivos por redondeo.
  /**
   * 🆕 Abre los Comprobantes de Ingreso del participante en una nueva pestaña,
   * filtrados por documento del participante + projectId (mismo patrón que el
   * dashboard de auditoría). El co-filtro por proyecto evita ver pagos del
   * mismo participante en otros proyectos.
   */
  goToReceipts(): void {
    const e: any = (this as any).enrollment;
    if (!e) {
      this.toastService.showToast('error', 'La inscripción aún no se cargó.');
      return;
    }
    const documentId = e?.participant?.documentId
                    ?? e?.participant?.documentNumber
                    ?? '';
    const projectId = e?.project?.id ?? null;
    const queryParams: any = { search: documentId || String(e.id) };
    if (projectId) queryParams.projectId = projectId;
    const tree = this.router.createUrlTree(
      ['/dashboard/income-receipts/individual'],
      { queryParams }
    );
    window.open(this.router.serializeUrl(tree), '_blank');
  }

  get needsAttentionInfo(): { needs: boolean; total: number; sumActive: number; delta: number } {
    const e: any = (this as any).enrollment;
    if (!e) return { needs: false, total: 0, sumActive: 0, delta: 0 };
    const total = Number(e.totalAmount ?? 0);
    const plans: any[] = e.paymentPlans ?? [];
    // El status===6 (Reversed) se filtra a la mano porque a veces aparece sin isActive
    const sumActive = plans
      .filter((p: any) => p?.isActive !== false)
      .reduce((acc: number, p: any) => acc + Number(p?.amountToPay ?? 0), 0);
    const delta = total - sumActive;
    return { needs: Math.abs(delta) > 0.01, total, sumActive, delta };
  }

  // 🆕 ====== Recálculo masivo previo a "Editar plan" ======
  isRecalculatingAll = false;

  /** 🆕 Recalcula pagos de todas las cuotas y abre el editor de plan (nueva pestaña, como antes). */
  onEditPlanClick(): void {
    if (this.isEditPaymentPlanDisabled() || this.isRecalculatingAll || !this.enrollment?.id) return;

    const returnTo = `/dashboard/enrollments/view-v2/${this.enrollment.id}`;
    const targetUrl = `/dashboard/enrollments/view/${this.enrollment.id}/edit-payment-plans?returnTo=${encodeURIComponent(returnTo)}`;

    // Sin permiso admin: comportamiento actual, sin recálculo.
    if (!this.isSuperAdmin) {
      window.open(targetUrl, '_blank');
      return;
    }

    this.isRecalculatingAll = true;
    this.adminService.recalculateAll(this.enrollment.id).subscribe({
      next: (resp: any) => {
        const data = resp?.data ?? resp;
        const changed = Number(data?.totalChanged ?? 0);
        if (changed > 0) {
          this.toastService.showToast('info', `Se actualizaron ${changed} cuota(s) antes de editar.`);
          this.loadEnrollment();
        }
        this.isRecalculatingAll = false;
        window.open(targetUrl, '_blank');
      },
      error: () => {
        this.isRecalculatingAll = false;
        this.toastService.showToast('warning', 'No se pudieron recalcular los pagos; abriendo el editor igualmente.');
        window.open(targetUrl, '_blank');
      }
    });
  }

  // 🆕 ====== Modal de edición del valor de matrícula ======
  editAmountModal: {
    open: boolean; loading: boolean; saving: boolean; confirmStep: boolean;
    context: any | null; newValue: number | null; comment: string;
  } = { open: false, loading: false, saving: false, confirmStep: false, context: null, newValue: null, comment: '' };

  openEditAmountModal(): void {
    if (!this.enrollment?.id) return;
    this.editAmountModal = { open: true, loading: true, saving: false, confirmStep: false, context: null, newValue: null, comment: '' };
    this.enrollmentService.getInscriptionContext(this.enrollment.id).subscribe({
      next: (resp: any) => {
        this.editAmountModal.context = resp?.data ?? resp;
        this.editAmountModal.newValue = Number(this.enrollment.totalAmount ?? 0);
        this.editAmountModal.loading = false;
      },
      error: () => {
        this.editAmountModal.open = false;
        this.toastService.showToast('error', 'No se pudo cargar el valor de referencia del proyecto.');
      }
    });
  }

  closeEditAmountModal(): void {
    if (this.editAmountModal.saving) return;
    this.editAmountModal.open = false;
  }

  /** 🆕 Preview en vivo del descuento resultante. El máximo del proyecto es solo informativo. */
  get editAmountPreview(): { discount: number; discountAmount: number; aboveReference: boolean } {
    const ctx = this.editAmountModal.context;
    const val = Number(this.editAmountModal.newValue ?? 0);
    const ref = Number(ctx?.referenceAmount ?? 0);
    if (!ctx || ref <= 0 || val <= 0) return { discount: 0, discountAmount: 0, aboveReference: false };
    const discountAmount = Math.max(0, ref - val);
    const discount = Math.round((discountAmount / ref) * 10000) / 100;
    return { discount, discountAmount, aboveReference: val > ref };
  }

  /** Paso 1: valida y pasa a la confirmación. */
  confirmEditAmount(): void {
    const val = Number(this.editAmountModal.newValue ?? 0);
    if (val <= 0) { this.toastService.showToast('error', 'El nuevo valor debe ser mayor a 0.'); return; }
    if ((this.editAmountModal.comment ?? '').trim().length < 10) {
      this.toastService.showToast('error', 'Comentario obligatorio (mín. 10 caracteres) para audit trail.');
      return;
    }
    this.editAmountModal.confirmStep = true;
  }

  /** Paso 2: guarda definitivamente. */
  saveEditAmount(): void {
    if (this.editAmountModal.saving || !this.enrollment?.id) return;
    this.editAmountModal.saving = true;
    this.enrollmentService.updateInscriptionAmount(
      this.enrollment.id,
      Number(this.editAmountModal.newValue),
      this.editAmountModal.comment.trim()
    ).subscribe({
      next: () => {
        this.editAmountModal.saving = false;
        this.editAmountModal.open = false;
        this.toastService.showToast('success',
          'Valor de matrícula actualizado. Recuerda editar el plan de pagos para redistribuir los saldos de las cuotas.');
        this.loadEnrollment();
      },
      error: (err: any) => {
        this.editAmountModal.saving = false;
        this.editAmountModal.confirmStep = false;
        const msg = typeof err?.error === 'string' ? err.error : (err?.error?.message ?? 'No se pudo actualizar el valor.');
        this.toastService.showToast('error', msg);
      }
    });
  }

  // 🆕 ====== Papelera de cuotas eliminadas (permiso ENROLLMENTS_PAYMENT_PLAN_TRASH) ======
  get canSeeTrash(): boolean {
    return this.authorizationService.hasPermission('ENROLLMENTS_PAYMENT_PLAN_TRASH');
  }

  trashModal: {
    open: boolean; loading: boolean; items: any[]; count: number;
    restoring: any | null; comment: string; saving: boolean;
  } = { open: false, loading: false, items: [], count: 0, restoring: null, comment: '', saving: false };

  /** Carga conteo/items de la papelera (se llama al cargar la inscripción). */
  loadTrashCount(): void {
    if (!this.canSeeTrash || !this.enrollment?.id) return;
    this.adminService.getTrash(this.enrollment.id).subscribe({
      next: (resp: any) => {
        const items = (resp?.data ?? resp) ?? [];
        this.trashModal.items = Array.isArray(items) ? items : [];
        this.trashModal.count = this.trashModal.items.length;
      },
      error: () => { /* papelera no disponible: silencioso */ }
    });
  }

  openTrashModal(): void {
    this.trashModal.open = true;
    this.trashModal.restoring = null;
    this.trashModal.comment = '';
    this.loadTrashCount();
  }

  askRestore(item: any): void {
    this.trashModal.restoring = item;
    this.trashModal.comment = '';
  }

  confirmRestore(): void {
    if (!this.trashModal.restoring || this.trashModal.saving) return;
    if ((this.trashModal.comment ?? '').trim().length < 10) {
      this.toastService.showToast('error', 'Comentario obligatorio (mín. 10 caracteres) para audit trail.');
      return;
    }
    this.trashModal.saving = true;
    this.adminService.restore(this.trashModal.restoring.id, this.trashModal.comment.trim()).subscribe({
      next: (resp: any) => {
        const data = resp?.data ?? resp;
        this.trashModal.saving = false;
        this.trashModal.restoring = null;
        this.toastService.showToast('success', data?.message ?? 'Cuota restaurada.');
        this.loadEnrollment();
        this.loadTrashCount();
      },
      error: (err: any) => {
        this.trashModal.saving = false;
        const msg = typeof err?.error === 'string' ? err.error : 'No se pudo restaurar la cuota.';
        this.toastService.showToast('error', msg);
        this.loadTrashCount();
      }
    });
  }

  // Estado del modal admin unificado. Cuando mode='menu' se muestra la grilla
  // de acciones; al elegir una se cambia el mode al específico.
  adminModal: {
    open: boolean;
    mode: 'menu' | 'renumber' | 'date' | 'delete' | 'status' | 'attach' | 'recalculate' | 'punish-remaining' | null;
    plan: any | null;
    newNumber: number | null;
    newDate: string;
    newStatus: 'PENDING' | 'PAID' | 'OVERDUE' | 'PARTIALLY_PAID' | 'LINK_GENERATED' | 'AUTO';
    comment: string;
    forceIfHasReceipts: boolean;
    attachReceiptId: number | null;
    forceFromOtherEnrollment: boolean;
  } = {
    open: false, mode: null, plan: null,
    newNumber: null, newDate: '', newStatus: 'AUTO',
    comment: '', forceIfHasReceipts: false,
    attachReceiptId: null, forceFromOtherEnrollment: false
  };

  /** Reset de campos del modal admin (todos en su default). */
  private resetAdminModal(plan: any): void {
    this.adminModal = {
      open: true,
      mode: null,
      plan,
      newNumber: null,
      newDate: '',
      newStatus: 'AUTO',
      comment: '',
      forceIfHasReceipts: false,
      attachReceiptId: null,
      forceFromOtherEnrollment: false
    };
  }

  /** 🆕 Abre el modal admin en modo "menú" — muestra la grilla de acciones. */
  openAdminMenu(plan: any): void {
    this.resetAdminModal(plan);
    this.adminModal.mode = 'menu';
  }

  /** 🆕 Cambia el modo activo dentro del modal y precarga los inputs por defecto. */
  selectAdminMode(mode: 'renumber' | 'date' | 'delete' | 'status' | 'attach' | 'recalculate' | 'punish-remaining'): void {
    const plan = this.adminModal.plan;
    if (!plan) return;
    // Resetear inputs específicos pero mantener el plan y comentario actual
    const keepComment = this.adminModal.comment;
    this.adminModal.mode = mode;
    this.adminModal.newNumber = mode === 'renumber' ? plan.installmentNumber : null;
    this.adminModal.newStatus = 'AUTO';
    this.adminModal.attachReceiptId = null;
    this.adminModal.forceFromOtherEnrollment = false;
    this.adminModal.forceIfHasReceipts = false;
    this.adminModal.comment = keepComment;
    if (mode === 'date') {
      const d = plan.dueDate ? new Date(plan.dueDate) : new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      this.adminModal.newDate = `${yyyy}-${mm}-${dd}`;
    } else {
      this.adminModal.newDate = '';
    }
  }

  /** Volver al menú desde un modo específico. */
  backToAdminMenu(): void {
    this.adminModal.mode = 'menu';
  }

  openAdminRenumber(plan: any): void {
    this.resetAdminModal(plan);
    this.adminModal.mode = 'renumber';
    this.adminModal.newNumber = plan.installmentNumber;
  }

  openAdminChangeDate(plan: any): void {
    this.resetAdminModal(plan);
    this.adminModal.mode = 'date';
    const d = plan.dueDate ? new Date(plan.dueDate) : new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    this.adminModal.newDate = `${yyyy}-${mm}-${dd}`;
  }

  openAdminDelete(plan: any): void {
    this.resetAdminModal(plan);
    this.adminModal.mode = 'delete';
  }

  openAdminChangeStatus(plan: any): void {
    this.resetAdminModal(plan);
    this.adminModal.mode = 'status';
  }

  openAdminAttachReceipt(plan: any): void {
    this.resetAdminModal(plan);
    this.adminModal.mode = 'attach';
  }

  closeAdminModal(): void {
    this.adminModal.open = false;
  }

  confirmAdminAction(): void {
    if (!this.adminModal.plan || !this.adminModal.mode || this.adminModal.mode === 'menu') return;
    const c = (this.adminModal.comment || '').trim();
    // Recalculate es inocuo: no exige comentario.
    if (this.adminModal.mode !== 'recalculate' && c.length < 10) {
      this.toastService.showToast('error', 'Comentario obligatorio (mín. 10 caracteres) para audit trail.');
      return;
    }

    const planId = this.adminModal.plan.id;
    this.loadingService.show();

    let req$;
    if (this.adminModal.mode === 'renumber') {
      if (!this.adminModal.newNumber || this.adminModal.newNumber <= 0) {
        this.loadingService.hide();
        this.toastService.showToast('error', 'Número de cuota debe ser > 0.');
        return;
      }
      req$ = this.adminService.renumber(planId, {
        newInstallmentNumber: this.adminModal.newNumber,
        comment: c
      });
    } else if (this.adminModal.mode === 'date') {
      if (!this.adminModal.newDate) {
        this.loadingService.hide();
        this.toastService.showToast('error', 'Fecha requerida.');
        return;
      }
      req$ = this.adminService.changeDueDate(planId, {
        newDueDate: new Date(this.adminModal.newDate).toISOString(),
        comment: c
      });
    } else if (this.adminModal.mode === 'status') {
      req$ = this.adminService.changeStatus(planId, {
        newStatus: this.adminModal.newStatus,
        comment: c
      });
    } else if (this.adminModal.mode === 'recalculate') {
      req$ = this.adminService.recalculateStatus(planId);
    } else if (this.adminModal.mode === 'punish-remaining') {
      req$ = this.adminService.punishRemaining(planId, { comment: c });
    } else if (this.adminModal.mode === 'attach') {
      if (!this.adminModal.attachReceiptId || this.adminModal.attachReceiptId <= 0) {
        this.loadingService.hide();
        this.toastService.showToast('error', 'ID del comprobante requerido.');
        return;
      }
      req$ = this.adminService.attachReceipt(planId, {
        receiptId: this.adminModal.attachReceiptId,
        comment: c,
        forceFromOtherEnrollment: this.adminModal.forceFromOtherEnrollment
      });
    } else {
      req$ = this.adminService.delete(planId, {
        comment: c,
        forceIfHasReceipts: this.adminModal.forceIfHasReceipts
      });
    }

    req$.subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        this.toastService.showToast('success', data?.message ?? 'Operación aplicada.');
        this.closeAdminModal();
        this.loadEnrollment();
      },
      error: (err) => {
        const msg = err?.error?.message ?? err?.error ?? 'Error aplicando operación.';
        this.toastService.showToast('error', typeof msg === 'string' ? msg : 'Error.');
      },
      complete: () => this.loadingService.hide()
    });
  }

  /**
   * 🆕 Drag & drop de cuotas (super-admin). Reordena el array local de forma
   * optimista, llama al backend para persistir, y si falla recarga la inscripción.
   */
  onPlanDrop(event: CdkDragDrop<any[]>): void {
    if (!this.isSuperAdmin) return;
    if (event.previousIndex === event.currentIndex) return;
    if (!this.enrollment?.paymentPlans?.length || !this.enrollment.id) return;

    const plans = this.enrollment.paymentPlans;
    moveItemInArray(plans, event.previousIndex, event.currentIndex);
    // Renumeramos en vivo para feedback inmediato (igualito a lo que hace el backend)
    plans.forEach((p: any, idx: number) => p.installmentNumber = idx + 1);

    const orderedIds = plans.map((p: any) => p.id);
    this.loadingService.show();
    this.adminService.reorder(this.enrollment.id, orderedIds).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        this.toastService.showToast('success', data?.message ?? 'Cuotas reordenadas.');
        this.loadEnrollment();
      },
      error: (err) => {
        const msg = err?.error?.message ?? err?.error ?? 'No se pudo reordenar.';
        this.toastService.showToast('error', typeof msg === 'string' ? msg : 'Error.');
        // Revertir: recargar desde backend
        this.loadEnrollment();
      },
      complete: () => this.loadingService.hide()
    });
  }

  ngOnInit(): void {
    this.loadEnrollment();
  }

  // ====== Append observation (no edit) ======
  isAddingObservation = false;
  newObservationDraft = '';
  isSavingObservation = false;
  startAddingObservation(): void {
    this.newObservationDraft = '';
    this.isAddingObservation = true;
  }
  cancelAddingObservation(): void {
    this.isAddingObservation = false;
    this.newObservationDraft = '';
  }
  saveNewObservation(): void {
    const note = (this.newObservationDraft || '').trim();
    if (note.length < 3) {
      this.toastService.showToast('warning', 'La observación debe tener al menos 3 caracteres.');
      return;
    }
    const id = this.enrollment?.id;
    if (!id) return;
    this.isSavingObservation = true;
    this.loadingService.show();
    this.enrollmentService.appendObservation(id, note).subscribe({
      next: () => {
        this.toastService.showToast('success', 'Observación agregada.');
        this.isAddingObservation = false;
        this.newObservationDraft = '';
        this.isSavingObservation = false;
        this.loadEnrollment();
      },
      error: (err) => {
        this.isSavingObservation = false;
        const msg = err?.error?.message ?? err?.error ?? 'No se pudo agregar la observación.';
        this.toastService.showToast('error', msg);
        this.loadingService.hide();
      }
    });
  }

  loadEnrollment(): void {
    const enrollmentId = this.route.snapshot.params['id'];

    if (!enrollmentId) {
      this.toastService.showToast('error', 'ID de inscripción no proporcionado.');
      return;
    }

    this.loadingService.show();
    this.enrollmentService.getById(enrollmentId).subscribe({
      next: (response) => {
        this.enrollment = response.data;
        // 🆕 Inicializar todas las filas como colapsadas por defecto
        if (this.enrollment?.paymentPlans) {
          this.enrollment.paymentPlans.forEach((plan: any) => {
            plan.isCollapsed = true;
          });
        }
        // 🆕 Cargar ingresos generales después de cargar la inscripción
        this.loadGeneralIncomeReceipts(enrollmentId);
        // 🆕 Cargar historial de movimientos de cartera
        this.loadMovementHistory(enrollmentId);
        // 🆕 Cargar devoluciones relacionadas con la inscripción
        this.loadRefundVouchers(enrollmentId);
        // 🆕 Cargar última cotización vigente
        this.loadCurrentExchangeRate();
        // 🆕 Cargar conteo de papelera (solo con permiso TRASH)
        this.loadTrashCount();
        // Inicializar el estado seleccionado del dropdown
        this.initSelectedEnrollmentStatus();
        this.loadingService.hide();
      },
      error: (err) => {
        console.error('Error al cargar la inscripción:', err);
        this.toastService.showToast('error', 'No se pudo cargar la inscripción.');
        this.loadingService.hide();
      }
    });
  }

  // Helper para extraer datos de estructura paginada o array directo
  private extractData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  /**
   * 🆕 Carga los ingresos generales (sin plan de pago) de la inscripción
   * Estos son pagos por servicios adicionales, certificados, etc.
   */
  loadGeneralIncomeReceipts(enrollmentId: number): void {
    this.enrollmentService.getGeneralIncomeReceipts(enrollmentId).subscribe({
      next: (response) => {
        this.generalIncomeReceipts = this.extractData(response);
        console.log('📋 Ingresos generales cargados:', this.generalIncomeReceipts.length);
      },
      error: (err) => {
        console.error('Error al cargar ingresos generales:', err);
        this.generalIncomeReceipts = [];
        // No mostrar error al usuario, solo log, ya que es opcional
      }
    });
  }

  /**
   * 🆕 Carga el historial de movimientos de cartera de la inscripción
   */
  loadMovementHistory(enrollmentId: number): void {
    this.portfolioMovementService.getMovementHistory(enrollmentId).subscribe({
      next: (response) => {
        this.movementHistory = this.extractData(response);
        console.log('📋 Historial de movimientos cargado:', this.movementHistory.length);
      },
      error: (err) => {
        console.error('Error al cargar historial de movimientos:', err);
        this.movementHistory = [];
      }
    });
  }

  /**
   * 🆕 Carga las devoluciones relacionadas con la inscripción
   */
  loadRefundVouchers(enrollmentId: number): void {
    this.refundVouchersService.getByEnrollmentId(enrollmentId).subscribe({
      next: (response) => {
        if (response.isSuccess && response.data) {
          this.refundVouchers = Array.isArray(response.data) ? response.data : [];
          console.log('📋 Devoluciones cargadas:', this.refundVouchers.length);
        } else {
          this.refundVouchers = [];
        }
      },
      error: (err) => {
        console.error('Error al cargar devoluciones:', err);
        this.refundVouchers = [];
      }
    });
  }

  /**
   * 🆕 Verifica si hay historial de movimientos
   */
  hasMovementHistory(): boolean {
    return this.movementHistory && this.movementHistory.length > 0;
  }

  /**
   * 🆕 Obtiene el icono del tipo de movimiento
   */
  getMovementIcon(movementType: number | string): string {
    const type = Number(movementType);
    switch (type) {
      case PortfolioMovementType.Withdrawal: return '⚠️';
      case PortfolioMovementType.WithdrawalReversal: return '✅';
      case PortfolioMovementType.Deferral: return '⏸️';
      default: return '📋';
    }
  }

  /**
   * 🆕 Obtiene la clase CSS del tipo de movimiento
   */
  getMovementClass(movementType: number | string): string {
    const type = Number(movementType);
    switch (type) {
      case PortfolioMovementType.Withdrawal: return 'bg-red-100 border-red-400 text-red-800';
      case PortfolioMovementType.WithdrawalReversal: return 'bg-green-100 border-green-400 text-green-800';
      case PortfolioMovementType.Deferral: return 'bg-orange-100 border-orange-400 text-orange-800';
      default: return 'bg-gray-100 border-gray-400 text-gray-800';
    }
  }

  /**
   * 🆕 Obtiene el color del punto en la timeline
   */
  getTimelineDotClass(movementType: number | string): string {
    const type = Number(movementType);
    switch (type) {
      case PortfolioMovementType.Withdrawal: return 'bg-red-500';
      case PortfolioMovementType.WithdrawalReversal: return 'bg-green-500';
      case PortfolioMovementType.Deferral: return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  }

  /**
   * 🆕 Cambia el estado del cobro automático (pide confirmación antes de aplicar)
   */
  toggleAutoPayment(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const enabled = checkbox.checked;

    // Revertir el checkbox visualmente hasta que el usuario confirme
    checkbox.checked = !enabled;

    this.pendingAutoPaymentValue = enabled;
    this.autoPaymentConfirmationConfig = {
      title: enabled ? '¿Activar cobro automático?' : '¿Desactivar cobro automático?',
      message: enabled
        ? 'A partir de ahora el sistema enviará links de pago automáticamente por email para las cuotas pendientes. ¿Deseas continuar?'
        : 'Los pagos de las cuotas pendientes deberán gestionarse manualmente y no se enviarán links de pago por email. ¿Deseas continuar?',
      type: enabled ? 'info' : 'warning',
      confirmText: enabled ? 'Sí, activar' : 'Sí, desactivar',
      cancelText: 'Cancelar',
      showIcon: true,
      icon: enabled ? 'fa-envelope' : 'fa-envelope-open'
    };
    this.showAutoPaymentConfirmationModal = true;
  }

  onAutoPaymentConfirmed(): void {
    this.showAutoPaymentConfirmationModal = false;
    const enabled = this.pendingAutoPaymentValue;

    this.loadingService.show();
    this.enrollmentService.updateAutoPayment(this.enrollment.id, enabled).subscribe({
      next: () => {
        this.loadingService.hide();
        this.enrollment.autoGeneratePaymentLinks = enabled;
        if (enabled) {
          this.toastService.showSuccess('Cobro automático activado. Se enviarán links de pago por email.');
        } else {
          this.toastService.showWarning('Cobro automático desactivado. Los pagos se gestionarán manualmente.');
        }
      },
      error: (err) => {
        this.loadingService.hide();
        this.toastService.showError('Error al actualizar el cobro automático: ' + (err.message || 'Error desconocido'));
      }
    });
  }

  onAutoPaymentCancelled(): void {
    this.showAutoPaymentConfirmationModal = false;
  }

  getPaymentStatusClass(status: string) {
    return {
      'bg-white': true,
      'bg-green-50': status === 'PAID',
      'bg-yellow-50': status === 'PENDING',
      'bg-red-50': status === 'OVERDUE' || status === 'CANCELED'
    };
  }

  // Métodos nuevos para el resumen financiero
  getCompletedPayments(): number {
    return this.enrollment.paymentPlans.filter((p: any) =>
      p.paymentStatus === 'PAID').length;
  }

  getPendingPayments(): number {
    return this.enrollment.paymentPlans.filter((p: any) =>
      ['PENDING', 'OVERDUE'].includes(p.paymentStatus)).length;
  }

  /**
   * Valor de la matrícula de la inscripción.
   *
   * ⚠️ Fuente de verdad: `Enrollment.TotalAmount` (campo en la tabla), NO la suma
   * de las cuotas. Por qué: las cuotas pueden divergir del total real cuando:
   *   - Hubo cartera castigada y la cuota quedó simbólica ($1)
   *   - Operaciones aplicó descuento que ajustó el TotalAmount sin re-versionar
   *   - Plan legacy con duplicados o saldos cerrados parcialmente
   *
   * Si por algún motivo `enrollment.totalAmount` no viene, hacemos fallback a la
   * suma de cuotas para no mostrar 0 — pero queda explícito en el método.
   */
  getTotalAmount(): number {
    if (this.enrollment?.totalAmount != null && this.enrollment.totalAmount > 0) {
      return this.enrollment.totalAmount;
    }
    // Fallback defensivo (no debería ocurrir en condiciones normales)
    return (this.enrollment?.paymentPlans ?? [])
      .reduce((acc: number, plan: any) => acc + (plan.amountToPay || 0), 0);
  }

  /**
   * Suma de cuotas activas — sirve para verificar consistencia con el
   * TotalAmount de la inscripción. Si difieren, hay un descuadre real.
   */
  getActiveInstallmentsSum(): number {
    return (this.enrollment?.paymentPlans ?? [])
      .reduce((acc: number, plan: any) => acc + (plan.amountToPay || 0), 0);
  }

  /** True cuando el TotalAmount diverge de la suma de cuotas activas. */
  hasTotalAmountMismatch(): boolean {
    if (!this.enrollment) return false;
    return Math.abs(this.getTotalAmount() - this.getActiveInstallmentsSum()) > 1;
  }

  getOverdueAmount(): number {
    return this.enrollment.paymentPlans
      .filter((p: any) => this.isPaymentOverdue(p) && !p.isPunished)
      .reduce((acc: number, plan: any) =>
        acc + (plan.amountToPay - plan.amountPaid), 0);
  }

  getCastigadoAmount(): number {
    if (!this.enrollment?.paymentPlans) return 0;
    return this.enrollment.paymentPlans
      .filter((p: any) => p.isPunished)
      .reduce((acc: number, plan: any) =>
        acc + (plan.amountToPay - plan.amountPaid), 0);
  }

  getCastigadoCount(): number {
    if (!this.enrollment?.paymentPlans) return 0;
    return this.enrollment.paymentPlans.filter((p: any) => p.isPunished).length;
  }

  /**
   * Saldo pendiente real (excluye cartera castigada). El campo
   * enrollment.remainingBalance del backend incluye los castigados para no
   * romper otros consumidores; aquí lo restamos solo para mostrar en pantalla.
   */
  getSaldoPendienteSinCastigo(): number {
    if (!this.enrollment) return 0;
    return (this.enrollment.remainingBalance || 0) - this.getCastigadoAmount();
  }

  // Métodos para el estado de los pagos
  isPaymentOverdue(plan: any): boolean {
    const dueDate = new Date(plan.dueDate);
    return dueDate < this.today && plan.paymentStatus !== 'PAID';
  }

  getDaysOverdue(plan: any): number {
    const dueDate = new Date(plan.dueDate);
    const diffTime = Math.abs(this.today.getTime() - dueDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  checkCurrentPayment(plan: any): boolean {
    const dueDate = new Date(plan.dueDate);
    return dueDate > this.today &&
      !this.enrollment.paymentPlans.some((p: any) =>
        new Date(p.dueDate) <= this.today && p.paymentStatus !== 'PAID'
      );
  }

  // Métodos existentes modificados
  getPaymentStatusLabel(status: string): string {
    switch (status) {
      case "PENDING":
        return 'Pendiente';
      case "PAID":
        return 'Pagado';
      case "OVERDUE":
        return 'Vencido';
      case "PARTIALLY_PAID":
        return 'Parcialmente Pagado';
      case "LINK_GENERATED":
        return 'Link generado';
      case "CANCELED":
        return 'Cancelado';
      default:
        return 'Desconocido';
    }
  }

  toggleCollapse(plan: any) {
    if (typeof plan.isCollapsed === 'undefined') {
      plan.isCollapsed = true; // Por defecto colapsado
    }
    plan.isCollapsed = !plan.isCollapsed;

    // Al expandir, cargar webhooks y emails si el plan tiene payment link
    if (!plan.isCollapsed && (plan.paymentLinkId || plan.paymentLinkWompiId) && !this.webhooksByPlan[plan.id]) {
      this.loadWebhooksForPlan(plan);
    }
    if (!plan.isCollapsed && plan.paymentLinkId && !this.emailsByPlan[plan.id]) {
      this.loadEmailsForPlan(plan);
    }
  }

  /**
   * 🆕 Cambia el tab activo para un plan específico
   */
  setPlanTab(planId: number, tab: string): void {
    this.planTabs[planId] = tab;
  }

  /**
   * 🆕 Obtiene el tab activo para un plan específico
   * Si no hay tab establecido, determina el tab inicial según qué información tiene disponible
   */
  getPlanTab(planId: number): string {
    if (this.planTabs[planId]) {
      return this.planTabs[planId];
    }

    // Determinar tab inicial según qué información tiene disponible
    const plan = this.enrollment?.paymentPlans?.find((p: any) => p.id === planId);
    if (!plan) return 'receipts';

    const isReversed = this.enrollment?.status === 6 || this.enrollment?.status === 'Reversed';

    // Si la inscripción está reversada, priorizar pestaña de anulados
    if (isReversed && this.getCancelledReceipts(plan).length > 0) {
      this.planTabs[planId] = 'cancelled';
      return 'cancelled';
    } else if (this.getActiveReceipts(plan).length > 0) {
      this.planTabs[planId] = 'receipts';
      return 'receipts';
    } else if (plan.paymentLinkUrl) {
      this.planTabs[planId] = 'payment-link';
      return 'payment-link';
    } else if (this.hasRefunds(plan)) {
      this.planTabs[planId] = 'refunds';
      return 'refunds';
    } else if (this.getCancelledReceipts(plan).length > 0) {
      this.planTabs[planId] = 'cancelled';
      return 'cancelled';
    } else {
      this.planTabs[planId] = 'receipts';
      return 'receipts';
    }
  }

  /**
   * 🆕 Verifica si un tab está activo para un plan
   */
  isPlanTabActive(planId: number, tab: string): boolean {
    return this.getPlanTab(planId) === tab;
  }

  formatCurrencyValue(amount: number, symbol?: string, code?: string): string {
    if (!amount && amount !== 0) return '';

    // Si no tenemos símbolo o código, usar por defecto COP
    const currencySymbol = symbol || '$';
    const currencyCode = code || 'COP';

    // Formatear el número con separador de miles
    const formattedAmount = new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    // Retornar con símbolo y código
    return `${currencySymbol}${formattedAmount} (${currencyCode})`;
  }

  getProjectInscriptionAmount(): string {
    if (!this.enrollment?.project) return '';

    const currencyCode = this.enrollment.currencyCode || 'COP';
    const symbol = this.enrollment.currencySymbol || '$';

    let amount = 0;
    if (currencyCode === 'COP') {
      amount = this.enrollment.project.inscriptionAmountCOP || 0;
    } else if (currencyCode === 'USD') {
      amount = this.enrollment.project.inscriptionAmountUSD || 0;
    } else {
      // Para otras monedas, usar USD como base
      amount = this.enrollment.project.inscriptionAmountUSD || 0;
    }

    return this.formatCurrencyValue(amount, symbol, currencyCode);
  }

  /**
   * Maneja la desactivación de un payment link
   * Recarga los datos de la inscripción para reflejar los cambios
   */
  onPaymentLinkDeactivated(): void {
    // Recargar los datos de la inscripción para reflejar el cambio de estado
    this.loadEnrollment();
  }

  /**
   * 🆕 Verifica si la inscripción es internacional
   */
  get isInternationalEnrollment(): boolean {
    return this.enrollment?.currencyCode &&
      this.enrollment.currencyCode !== 'COP' &&
      this.enrollment.currencyCode !== null;
  }

  /**
   * 🆕 Obtiene el código de moneda de la inscripción
   */
  get enrollmentCurrencyCode(): string | null {
    return this.enrollment?.currencyCode || this.enrollment?.project?.currency?.code || 'COP';
  }

  getEnrollmentExchangeRate(): number | null {
    return this.enrollment?.exchangeRateUsed || this.enrollment?.exchangeRate || null;
  }

  /**
   * 🆕 Obtiene la tasa de cambio correcta para un plan de pago
   * Prioridad: tasa del comprobante (si está pagado) > última cotización vigente (si está pendiente) > tasa de la inscripción
   */
  getPlanExchangeRate(plan: any): number | null {
    if (!this.isInternationalEnrollment) return null;

    // Si el plan está pagado y tiene comprobantes, usar la tasa del primer comprobante (histórica)
    if (plan.paymentStatus === 'PAID' && plan.incomeReceipts && plan.incomeReceipts.length > 0) {
      const receipt = plan.incomeReceipts[0]; // Usar el primer comprobante
      if (receipt.exchangeRate && receipt.exchangeRate !== null && receipt.exchangeRate !== 1) {
        return receipt.exchangeRate;
      }
    }

    // 🆕 Para planes pendientes, usar la última cotización vigente si está disponible
    if (plan.paymentStatus !== 'PAID' && this.currentExchangeRate && this.currentExchangeRate !== 1) {
      return this.currentExchangeRate;
    }

    // Fallback: usar la tasa de la inscripción
    return this.getEnrollmentExchangeRate();
  }

  /**
   * 🆕 Carga la última cotización vigente para la moneda de la inscripción
   */
  private loadCurrentExchangeRate(): void {
    if (!this.enrollmentCurrencyCode || this.enrollmentCurrencyCode === 'COP') {
      this.currentExchangeRate = 1;
      return;
    }

    this.exchangeRateService.getLatestRate(this.enrollmentCurrencyCode, 'COP').subscribe({
      next: (response: any) => {
        if (response.isSuccess && response.rate > 0) {
          this.currentExchangeRate = response.rate;
          console.log(`✅ Última cotización vigente cargada: ${this.currentExchangeRate} para ${this.enrollmentCurrencyCode}`);
        } else {
          // Fallback a tasa de la inscripción
          this.currentExchangeRate = this.getEnrollmentExchangeRate();
        }
      },
      error: (err) => {
        console.error('Error obteniendo última cotización vigente:', err);
        // Fallback a tasa de la inscripción
        this.currentExchangeRate = this.getEnrollmentExchangeRate();
      }
    });
  }

  /**
   * 🆕 Calcula el equivalente en COP de un monto para un plan de pago específico
   * Usa la tasa de cambio del comprobante si está pagado, o la de la inscripción si está pendiente
   */
  calculatePlanCOPEquivalent(plan: any, amount: number): string {
    if (!this.isInternationalEnrollment || !amount) return '';

    const rate = this.getPlanExchangeRate(plan);
    if (!rate) return 'N/A';

    const copAmount = amount * rate;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(copAmount);
  }

  /**
   * 🆕 Formatea la tasa de cambio de un plan de pago para mostrar
   */
  formatPlanExchangeRate(plan: any): string {
    const rate = this.getPlanExchangeRate(plan);
    if (!rate) return 'N/A';
    return new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(rate);
  }

  /**
   * Verifica si un recibo tiene tasa de cambio internacional
   */
  hasReceiptExchangeRate(receipt: any): boolean {
    return receipt?.exchangeRate && receipt.exchangeRate !== null && receipt.exchangeRate !== 1;
  }

  /**
   * Obtiene el equivalente en COP de un monto para un recibo con tasa de cambio
   */
  getReceiptCOPEquivalent(receipt: any, amount: number): string {
    if (!this.hasReceiptExchangeRate(receipt) || !receipt?.exchangeRate) {
      return 'N/A';
    }

    const copAmount = amount * receipt.exchangeRate;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(copAmount);
  }

  /**
   * 🆕 Calcula el total de ingresos generales
   */
  getTotalGeneralIncome(): number {
    if (!this.generalIncomeReceipts || this.generalIncomeReceipts.length === 0) {
      return 0;
    }
    return this.generalIncomeReceipts.reduce((sum, receipt) => sum + (receipt.netAmount || 0), 0);
  }

  // ============== MÉTODOS PARA CARTERA CASTIGADA ==============

  /**
   * Verifica si la inscripción tiene cuotas castigadas
   */
  hasPunishedInstallments(): boolean {
    if (!this.enrollment?.paymentPlans) return false;
    return this.enrollment.paymentPlans.some((p: any) => p.isPunished);
  }

  /**
   * Obtiene las cuotas castigadas
   */
  getPunishedInstallments(): any[] {
    if (!this.enrollment?.paymentPlans) return [];
    return this.enrollment.paymentPlans.filter((p: any) => p.isPunished);
  }

  /**
   * Calcula el total de cartera castigada
   */
  getTotalPunishedAmount(): number {
    return this.getPunishedInstallments()
      .reduce((sum: number, p: any) => sum + (p.amountToPay - (p.amountPaid || 0)), 0);
  }

  /**
   * Obtiene la etiqueta del estado de la inscripción
   */
  getEnrollmentStatusLabel(status: number | string): string {
    const statusMap: { [key: string]: string } = {
      '0': 'Pendiente',
      '1': 'Activa',
      '2': 'Retirada',
      '3': 'Aplazada',
      '4': 'Completada',
      '5': 'Refinanciada',
      '6': 'Reversada',
      '7': 'Reintegro',
      'Pending': 'Pendiente',
      'Active': 'Activa',
      'Withdrawn': 'Retirada',
      'Deferred': 'Aplazada',
      'Completed': 'Completada',
      'Refinanced': 'Refinanciada',
      'Reversed': 'Reversada',
      'Refund': 'Reintegro',
      '8': 'Cesión de Cupo',
      'SlotTransfer': 'Cesión de Cupo'
    };
    return statusMap[String(status)] || 'Desconocido';
  }

  /**
   * Obtiene la clase CSS del estado de la inscripción
   */
  getEnrollmentStatusClass(status: number | string): string {
    const statusStr = String(status);
    if (statusStr === '0' || statusStr === 'Pending') return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    if (statusStr === '1' || statusStr === 'Active') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    if (statusStr === '2' || statusStr === 'Withdrawn') return 'bg-red-100 text-red-800 border-red-200';
    if (statusStr === '3' || statusStr === 'Deferred') return 'bg-orange-100 text-orange-800 border-orange-200';
    if (statusStr === '4' || statusStr === 'Completed') return 'bg-blue-100 text-blue-800 border-blue-200';
    if (statusStr === '5' || statusStr === 'Refinanced') return 'bg-indigo-100 text-indigo-800 border-indigo-200';
    if (statusStr === '6' || statusStr === 'Reversed') return 'bg-gray-100 text-gray-800 border-gray-200';
    if (statusStr === '7' || statusStr === 'Refund') return 'bg-purple-100 text-purple-800 border-purple-200';
    if (statusStr === '8' || statusStr === 'SlotTransfer') return 'bg-teal-100 text-teal-800 border-teal-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  }

  /**
   * Verifica si la inscripción está retirada o aplazada
   */
  isEnrollmentWithdrawnOrDeferred(): boolean {
    if (!this.enrollment) return false;
    const status = String(this.enrollment.status);
    return status === '2' || status === '3' ||
      status === 'Withdrawn' || status === 'Deferred';
  }

  /**
   * Obtiene la etiqueta del tipo de movimiento
   */
  getMovementTypeLabel(movementType: number | string): string {
    const typeMap: { [key: string]: string } = {
      '0': 'Sin movimiento',
      '1': 'Retiro',
      '2': 'Reversión de Retiro',
      '3': 'Aplazamiento',
      'None': 'Sin movimiento',
      'Withdrawal': 'Retiro',
      'WithdrawalReversal': 'Reversión de Retiro',
      'Deferral': 'Aplazamiento'
    };
    return typeMap[String(movementType)] || 'Desconocido';
  }

  // ============== GESTIÓN DE CASTIGO MANUAL ==============

  isPunishMode: boolean = false;
  selectedPlanIds: number[] = [];
  punishReason: string = '';
  punishNotes: string = '';

  togglePunishMode(): void {
    if (!this.isPunishMode && this.areAllPaymentPlansPaid()) {
      return;
    }
    this.isPunishMode = !this.isPunishMode;
    this.selectedPlanIds = [];
    this.punishReason = '';
    this.punishNotes = '';
  }

  areAllPaymentPlansPaid(): boolean {
    const plans = this.enrollment?.paymentPlans;
    if (!plans || plans.length === 0) return false;
    return plans.every((p: any) => p.paymentStatus === 'PAID');
  }

  /**
   * Deshabilita "Editar plan de pagos" SOLO cuando todas las cuotas están pagadas
   * Y no queda saldo pendiente. Si hay diferencia entre el valor de la inscripción
   * y la suma de cuotas (saldo pendiente > 0), se permite editar para agregar la
   * cuota faltante aunque las existentes ya estén pagas.
   */
  isEditPaymentPlanDisabled(): boolean {
    return this.areAllPaymentPlansPaid() && this.getSaldoPendienteSinCastigo() <= 0;
  }

  togglePlanSelection(planId: number): void {
    const plan = this.enrollment.paymentPlans.find((p: any) => p.id === planId);
    if (plan && plan.paymentStatus === 'PAID') {
      return; // No permitir seleccionar cuotas pagadas
    }

    if (this.selectedPlanIds.includes(planId)) {
      this.selectedPlanIds = this.selectedPlanIds.filter(id => id !== planId);
    } else {
      this.selectedPlanIds.push(planId);
    }
  }

  isPlanSelected(planId: number): boolean {
    return this.selectedPlanIds.includes(planId);
  }

  selectAllPlans(): void {
    if (this.enrollment?.paymentPlans) {
      // Filtrar solo las cuotas no pagadas
      const availablePlans = this.enrollment.paymentPlans.filter((p: any) => p.paymentStatus !== 'PAID');

      if (this.selectedPlanIds.length === availablePlans.length) {
        this.selectedPlanIds = [];
      } else {
        this.selectedPlanIds = availablePlans.map((p: any) => p.id);
      }
    }
  }

  executePunishAction(action: 'punish' | 'unpunish'): void {
    if (this.selectedPlanIds.length === 0) {
      this.toastService.showToast('warning', 'Debes seleccionar al menos una cuota.');
      return;
    }

    if (!this.punishReason || this.punishReason.trim().length === 0) {
      this.toastService.showToast('warning', 'Debes ingresar una razón para esta acción.');
      return;
    }

    const request = {
      enrollmentId: this.enrollment.id,
      paymentPlanIds: this.selectedPlanIds,
      reason: this.punishReason,
      notes: this.punishNotes
    };

    this.loadingService.show();

    const observable = action === 'punish'
      ? this.portfolioMovementService.punishInstallments(request)
      : this.portfolioMovementService.unpunishInstallments(request);

    observable.subscribe({
      next: (response) => {
        if (response.isSuccess) {
          const message = response.data?.message || (action === 'punish' ? 'Cuotas castigadas exitosamente' : 'Cuotas descastigadas exitosamente');
          this.toastService.showToast('success', message);
          this.togglePunishMode();
          this.loadEnrollment(); // Recargar datos
        } else {
          this.toastService.showToast('error', response.errorMessage || 'Ocurrió un error al procesar la solicitud.');
        }
        this.loadingService.hide();
      },
      error: (err) => {
        console.error(`Error al ${action === 'punish' ? 'castigar' : 'descastigar'} cuotas:`, err);
        this.toastService.showToast('error', `Error al procesar la solicitud: ${err.message}`);
        this.loadingService.hide();
      }
    });
  }

  // Métodos auxiliares para el template V2
  getNextDueDate(): string {
    if (!this.enrollment?.paymentPlans) return '';
    const pendingPlans = this.enrollment.paymentPlans
      .filter((p: any) => p.paymentStatus !== 'PAID' && !p.isPunished)
      .sort((a: any, b: any) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());

    if (pendingPlans.length === 0) return '';
    const nextPlan = pendingPlans[0];
    return new Date(nextPlan.dueDate).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  getOverdueCount(): number {
    if (!this.enrollment?.paymentPlans) return 0;
    return this.enrollment.paymentPlans.filter((p: any) => this.isPaymentOverdue(p) && !p.isPunished).length;
  }

  getOverdueDays(): number {
    if (!this.enrollment?.paymentPlans) return 0;
    const overduePlans = this.enrollment.paymentPlans.filter((p: any) => this.isPaymentOverdue(p) && !p.isPunished);
    if (overduePlans.length === 0) return 0;
    const oldestOverdue = overduePlans.sort((a: any, b: any) =>
      new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())[0];
    return this.getDaysOverdue(oldestOverdue);
  }

  /**
   * Obtiene el número de planes disponibles para seleccionar (no pagados)
   */
  getAvailablePlansCount(): number {
    if (!this.enrollment?.paymentPlans) return 0;
    return this.enrollment.paymentPlans.filter((p: any) => p.paymentStatus !== 'PAID').length;
  }

  /**
   * Verifica si todos los planes disponibles están seleccionados
   */
  areAllPlansSelected(): boolean {
    const availableCount = this.getAvailablePlansCount();
    return availableCount > 0 && this.selectedPlanIds.length === availableCount;
  }

  /**
   * 🆕 Abre el modal con el detalle del movimiento
   */
  openMovementDetail(movement: PortfolioMovementResponse): void {
    this.selectedMovement = movement;
    this.showMovementModal = true;
  }

  /**
   * 🆕 Cierra el modal de detalle
   */
  closeMovementModal(): void {
    this.showMovementModal = false;
    this.selectedMovement = null;
  }

  /**
   * 🆕 Abre el modal de anulación
   */
  openCancellationModal(): void {
    this.cancellationReason = '';
    this.showCancellationModal = true;
  }

  /**
   * 🆕 Cierra el modal de anulación
   */
  closeCancellationModal(): void {
    this.showCancellationModal = false;
    this.cancellationReason = '';
  }

  /**
   * 🆕 Ejecuta la anulación
   */
  confirmCancellation(): void {
    if (!this.cancellationReason || this.cancellationReason.trim().length === 0) {
      this.toastService.showToast('warning', 'El motivo de anulación es obligatorio.');
      return;
    }

    this.isCancelling = true;
    this.loadingService.show();

    this.enrollmentService.cancel(this.enrollment.id, this.cancellationReason).subscribe({
      next: (response) => {
        this.loadingService.hide();
        this.isCancelling = false;
        this.closeCancellationModal();
        this.toastService.showSuccess('Inscripción anulada exitosamente.');
        this.loadEnrollment();
      },
      error: (err) => {
        this.loadingService.hide();
        this.isCancelling = false;
        console.error('Error al anular inscripción:', err);
        this.toastService.showError(err.message || 'Error al anular la inscripción.');
      }
    });
  }

  // ============== MÉTODOS PARA COMPROBANTES ANULADOS ==============

  /**
   * Obtiene los comprobantes activos: no anulados, activos (última versión), no eliminados
   * y que no estén pendientes de aprobación
   */
  getActiveReceipts(plan: any): any[] {
    if (!plan?.incomeReceipts) return [];
    return plan.incomeReceipts.filter((r: any) =>
      !r.isCancelled && r.isActive && !r.isDeleted && r.approvalStatus !== 'PendingApproval'
    );
  }

  /**
   * Fecha del último pago recibido en todo el plan de pagos: max(transactionDate || createdAt)
   * sobre comprobantes activos de todas las cuotas. null si no hay pagos.
   */
  getLastPaymentDate(): Date | null {
    if (!this.enrollment?.paymentPlans) return null;
    let latest: number | null = null;
    for (const plan of this.enrollment.paymentPlans) {
      for (const r of this.getActiveReceipts(plan)) {
        const raw = r.transactionDate || r.createdAt;
        if (!raw) continue;
        const t = new Date(raw).getTime();
        if (Number.isNaN(t)) continue;
        if (latest === null || t > latest) latest = t;
      }
    }
    return latest === null ? null : new Date(latest);
  }

  // 🆕 ====== Anulación de comprobante de ingreso desde la vista de inscripción ======

  /**
   * Indica si un comprobante puede anularse desde la lista de comprobantes activos.
   * - No debe estar ya anulado.
   * - No debe estar conciliado bancariamente.
   * - No debe estar pendiente de aprobación.
   * El backend valida adicionalmente el permiso `INCOME_CANCEL`.
   */
  canCancelReceipt(receipt: any): boolean {
    if (!receipt) return false;
    if (receipt.isCancelled) return false;
    if (receipt.isBankReconciled === true) return false;
    if (receipt.approvalStatus === 'PendingApproval') return false;
    return true;
  }

  openCancelReceiptModal(receipt: any): void {
    if (!this.canCancelReceipt(receipt)) return;
    this.cancelReceiptModal = {
      open: true,
      receipt,
      reason: '',
      submitting: false
    };
  }

  closeCancelReceiptModal(): void {
    if (this.cancelReceiptModal.submitting) return;
    this.cancelReceiptModal = { open: false, receipt: null, reason: '', submitting: false };
  }

  confirmCancelReceipt(): void {
    const receipt = this.cancelReceiptModal.receipt;
    if (!receipt || this.cancelReceiptModal.submitting) return;

    const reason = (this.cancelReceiptModal.reason || '').trim();
    if (!reason) {
      this.toastService.showToast('error', 'Debe especificar el motivo de anulación');
      return;
    }
    if (reason.length < 10) {
      this.toastService.showToast('error', 'El motivo debe tener al menos 10 caracteres');
      return;
    }

    this.cancelReceiptModal.submitting = true;
    this.loadingService.show();

    this.incomeReceiptsService.cancelIncomeReceipt(receipt.id, reason).subscribe({
      next: () => {
        this.toastService.showToast('success', '✅ Comprobante anulado exitosamente');
        this.cancelReceiptModal = { open: false, receipt: null, reason: '', submitting: false };
        // Recarga el enrollment para reflejar PaymentStatus de la cuota + receipt anulado.
        this.loadEnrollment();
      },
      error: (error: any) => {
        console.error('Error al anular comprobante:', error);
        const errorMessage = error?.error?.errorMessage
          || (typeof error?.error === 'string' ? error.error : null)
          || 'Error al anular el comprobante';
        this.toastService.showToast('error', errorMessage);
        this.cancelReceiptModal.submitting = false;
      },
      complete: () => {
        this.loadingService.hide();
      }
    });
  }

  /**
   * Obtiene los comprobantes anulados de un plan de pago
   */
  getCancelledReceipts(plan: any): any[] {
    if (!plan?.incomeReceipts) return [];
    return plan.incomeReceipts.filter((r: any) => r.isCancelled);
  }

  /**
   * Obtiene los comprobantes inactivos: versiones anteriores reemplazadas o eliminadas (no anulados)
   */
  getInactiveReceipts(plan: any): any[] {
    if (!plan?.incomeReceipts) return [];
    return plan.incomeReceipts.filter((r: any) =>
      !r.isCancelled && (!r.isActive || r.isDeleted || r.approvalStatus === 'PendingApproval')
    );
  }

  /**
   * Verifica si un plan tiene comprobantes anulados
   */
  hasCancelledReceipts(plan: any): boolean {
    return this.getCancelledReceipts(plan).length > 0;
  }

  /**
   * Verifica si un plan tiene comprobantes inactivos (versiones anteriores)
   */
  hasInactiveReceipts(plan: any): boolean {
    return this.getInactiveReceipts(plan).length > 0;
  }

  /**
   * Obtiene el total de monto anulado en un plan de pago
   */
  getTotalCancelledAmount(plan: any): number {
    const cancelledReceipts = this.getCancelledReceipts(plan);
    return cancelledReceipts.reduce((sum: number, r: any) => sum + (r.grossAmount || 0), 0);
  }

  // ============== MÉTODOS PARA DEVOLUCIONES ==============

  /**
   * 🆕 Obtiene las devoluciones relacionadas con un plan de pago específico
   * Basándose en los incomeReceiptIds de los refundVoucherItems
   */
  getRefundsForPlan(plan: any): any[] {
    if (!this.refundVouchers || this.refundVouchers.length === 0) return [];
    if (!plan?.incomeReceipts || plan.incomeReceipts.length === 0) return [];

    // Obtener los IDs de los comprobantes de ingreso del plan
    const planReceiptIds = plan.incomeReceipts.map((r: any) => r.id);

    // Buscar devoluciones que tengan items relacionados con estos comprobantes
    const relatedRefunds: any[] = [];

    this.refundVouchers.forEach((refund: any) => {
      if (refund.refundVoucherItems && refund.refundVoucherItems.length > 0) {
        const relatedItems = refund.refundVoucherItems.filter((item: any) => {
          // Verificar si el item está relacionado con algún comprobante del plan
          // Usar documentId directamente (más eficiente) o verificar incomeReceipts
          if (item.documentType === 'IncomeReceipt') {
            // Primero verificar documentId directamente
            if (planReceiptIds.includes(item.documentId)) {
              return true;
            }
            // También verificar en incomeReceipts array por compatibilidad
            if (item.incomeReceipts && item.incomeReceipts.length > 0) {
              return item.incomeReceipts.some((receipt: any) =>
                planReceiptIds.includes(receipt.incomeReceiptId) ||
                planReceiptIds.includes(receipt.id)
              );
            }
          }
          return false;
        });

        if (relatedItems.length > 0) {
          // Crear una copia del refund con solo los items relacionados
          relatedRefunds.push({
            ...refund,
            relatedItems: relatedItems,
            totalRefundedForPlan: relatedItems.reduce((sum: number, item: any) =>
              sum + (item.refundedAmount || 0), 0
            )
          });
        }
      }
    });

    return relatedRefunds;
  }

  /**
   * 🆕 Verifica si un plan tiene devoluciones asociadas
   */
  hasRefunds(plan: any): boolean {
    return this.getRefundsForPlan(plan).length > 0;
  }

  /**
   * 🆕 Obtiene el total de monto devuelto para un plan específico
   */
  getTotalRefundedForPlan(plan: any): number {
    const refunds = this.getRefundsForPlan(plan);
    return refunds.reduce((sum: number, refund: any) =>
      sum + (refund.totalRefundedForPlan || 0), 0
    );
  }

  /**
   * 🆕 Obtiene los items de devolución específicos para un comprobante de ingreso
   */
  getRefundItemsForReceipt(receiptId: number): any[] {
    if (!this.refundVouchers || this.refundVouchers.length === 0) return [];

    const items: any[] = [];

    this.refundVouchers.forEach((refund: any) => {
      if (refund.refundVoucherItems && refund.refundVoucherItems.length > 0) {
        refund.refundVoucherItems.forEach((item: any) => {
          if (item.documentType === 'IncomeReceipt' &&
            item.incomeReceipts &&
            item.incomeReceipts.some((r: any) => r.incomeReceiptId === receiptId)) {
            items.push({
              ...item,
              refundVoucher: refund
            });
          }
        });
      }
    });

    return items;
  }

  /**
   * 🆕 Calcula el total de comprobantes activos para un plan
   */
  getTotalActiveReceipts(plan: any): number {
    const activeReceipts = this.getActiveReceipts(plan);
    return activeReceipts.reduce((sum: number, r: any) => sum + (r.netAmount || 0), 0);
  }

  // ============== MÉTODOS PARA CAMBIAR ESTADO ==============

  /**
   * Obtiene las opciones de estado disponibles
   */
  getEnrollmentStatusOptions(): Array<{ value: number; label: string }> {
    return [
      { value: 0, label: 'Pendiente' },
      { value: 1, label: 'Activa' },
      { value: 2, label: 'Retirada' },
      { value: 3, label: 'Aplazada' },
      { value: 4, label: 'Completada' },
      { value: 5, label: 'Refinanciada' },
      { value: 6, label: 'Reversada' },
      { value: 7, label: 'Reintegro' }
    ];
  }

  /**
   * Maneja el cambio de estado de la inscripción - Abre el modal de confirmación
   */
  onStatusChange(newStatus: number): void {
    if (!this.enrollment) return;

    const currentStatus = typeof this.enrollment.status === 'string'
      ? this.getStatusValueFromString(this.enrollment.status)
      : this.enrollment.status;

    if (currentStatus === newStatus) {
      return; // No hay cambio
    }

    const statusLabel = this.enrollmentStatusOptions.find(opt => opt.value === newStatus)?.label || 'Desconocido';

    // Guardar el cambio pendiente
    this.pendingStatusChange = { newStatus, statusLabel };
    this.statusChangeReason = '';

    // Configurar si se requiere razón (Withdrawn, Deferred o Cesión de Cupo)
    this.showReasonInput = (newStatus === 2 || newStatus === 3 || newStatus === 8);

    // Configurar el modal
    this.statusConfirmationConfig = {
      title: 'Confirmar cambio de estado',
      message: this.showReasonInput
        ? `¿Está seguro de cambiar el estado de la inscripción a "${statusLabel}"?\n\nEste cambio requiere una justificación.`
        : `¿Está seguro de cambiar el estado de la inscripción a "${statusLabel}"?`,
      type: 'warning',
      confirmText: 'Sí, cambiar estado',
      cancelText: 'Cancelar',
      showIcon: true,
      icon: 'fa-exchange-alt'
    };

    // Mostrar el modal
    this.showStatusConfirmationModal = true;
  }

  /**
   * Ejecuta el cambio de estado después de la confirmación del modal
   */
  onStatusConfirmationConfirmed(): void {
    if (!this.pendingStatusChange || !this.enrollment) {
      this.onStatusConfirmationCancelled();
      return;
    }

    // Validar razón si es requerida
    if (this.showReasonInput && !this.statusChangeReason.trim()) {
      this.toastService.showToast('warning', 'Debes ingresar una razón para este cambio de estado.');
      return; // No cerrar el modal, permitir al usuario ingresar la razón
    }

    const { newStatus, statusLabel } = this.pendingStatusChange;
    const reason = this.showReasonInput ? this.statusChangeReason.trim() : '';

    // Cerrar el modal
    this.showStatusConfirmationModal = false;

    // Ejecutar el cambio
    this.loadingService.show();
    this.enrollmentService.updateStatus(this.enrollment.id, newStatus, reason).subscribe({
      next: (response) => {
        this.toastService.showToast('success', `Estado de la inscripción actualizado a "${statusLabel}" exitosamente.`);
        this.loadEnrollment(); // Recargar datos
        this.loadingService.hide();
        this.pendingStatusChange = null;
        this.statusChangeReason = '';
      },
      error: (err) => {
        console.error('Error al actualizar estado:', err);
        this.toastService.showToast('error', err.message || 'Error al actualizar el estado de la inscripción.');
        // Restaurar el valor anterior en el dropdown en caso de error
        this.selectedEnrollmentStatus = typeof this.enrollment.status === 'string'
          ? this.getStatusValueFromString(this.enrollment.status)
          : this.enrollment.status;
        this.loadingService.hide();
        this.pendingStatusChange = null;
        this.statusChangeReason = '';
      }
    });
  }

  /**
   * Cancela el cambio de estado
   */
  onStatusConfirmationCancelled(): void {
    this.showStatusConfirmationModal = false;

    // Restaurar el valor anterior en el dropdown
    if (this.enrollment) {
      this.selectedEnrollmentStatus = typeof this.enrollment.status === 'string'
        ? this.getStatusValueFromString(this.enrollment.status)
        : this.enrollment.status;
    }

    this.pendingStatusChange = null;
    this.statusChangeReason = '';
  }

  /**
   * Convierte un string de estado a su valor numérico
   */
  private getStatusValueFromString(status: string): number {
    const statusMap: { [key: string]: number } = {
      'Pending': 0,
      'Active': 1,
      'Withdrawn': 2,
      'Deferred': 3,
      'Completed': 4,
      'Refinanced': 5,
      'Reversed': 6,
      'Refund': 7,
      'SlotTransfer': 8
    };
    return statusMap[status] ?? 1; // Default a Active
  }

  /**
   * 🆕 Estado del botón de exportar Excel (para evitar doble-click)
   */
  isExportingExcel: boolean = false;

  /**
   * 🆕 Descarga el reporte Excel (.xlsx) de la inscripción individual.
   * Incluye ficha, KPIs, plan de pagos y comprobantes de ingreso.
   */
  exportExcel(): void {
    if (!this.enrollment?.id) {
      this.toastService.showToast('warning', 'La inscripción aún no está cargada.');
      return;
    }

    if (this.isExportingExcel) {
      return;
    }

    this.isExportingExcel = true;
    this.loadingService.show();

    this.enrollmentService.exportExcel(this.enrollment.id).subscribe({
      next: (blob: Blob) => {
        const today = new Date().toISOString().slice(0, 10);
        const fileName = `Inscripcion_${this.enrollment.id}_${today}.xlsx`;

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(url);

        this.toastService.showSuccess('Reporte Excel descargado correctamente.');
        this.loadingService.hide();
        this.isExportingExcel = false;
      },
      error: (err: any) => {
        console.error('Error al exportar Excel de la inscripción:', err);
        this.toastService.showError(err?.message || 'Error al generar el reporte Excel.');
        this.loadingService.hide();
        this.isExportingExcel = false;
      }
    });
  }

  /**
   * Obtiene el valor numérico del estado actual
   */
  getCurrentStatusValue(): number {
    if (!this.enrollment) return 1;
    if (typeof this.enrollment.status === 'number') {
      return this.enrollment.status;
    }
    return this.getStatusValueFromString(this.enrollment.status);
  }

  /**
   * Inicializa el valor seleccionado del dropdown de estado
   */
  private initSelectedEnrollmentStatus(): void {
    this.selectedEnrollmentStatus = this.getCurrentStatusValue();
  }

  /**
   * Verifica si el usuario tiene permiso para actualizar el estado de la inscripción
   */
  get canUpdateEnrollmentStatus(): boolean {
    return this.authorizationService.hasPermission('ENROLLMENTS_UPDATE');
  }

  // ============== EMAIL LOGS ==============

  loadEmailsForPlan(plan: any): void {
    if (!plan.paymentLinkId) return;

    this.emailsLoadingByPlan[plan.id] = true;

    this.emailLogService.searchEmailLogs({
      paymentLinkId: plan.paymentLinkId,
      pageSize: 50,
      orderBy: 'SentAt',
      orderDirection: 'desc'
    }).subscribe({
      next: (response) => {
        this.emailsByPlan[plan.id] = response.data;
        this.emailsLoadingByPlan[plan.id] = false;
      },
      error: (err) => {
        console.error('Error al cargar emails para plan', plan.id, err);
        this.emailsByPlan[plan.id] = [];
        this.emailsLoadingByPlan[plan.id] = false;
      }
    });
  }

  getEmailsForPlan(plan: any): EmailLogResponseDTO[] {
    return this.emailsByPlan[plan.id] || [];
  }

  hasEmails(plan: any): boolean {
    return this.emailsByPlan[plan.id] !== undefined && this.emailsByPlan[plan.id].length > 0;
  }

  isEmailsLoading(plan: any): boolean {
    return this.emailsLoadingByPlan[plan.id] === true;
  }

  // ============== WEBHOOKS WOMPI ==============

  /**
   * Carga los webhooks asociados al payment link de un plan de pago
   */
  loadWebhooksForPlan(plan: any): void {
    if (!plan.paymentLinkId && !plan.paymentLinkWompiId) return;

    this.webhooksLoadingByPlan[plan.id] = true;

    // Buscar por Wompi external ID (siempre presente en webhook) con fallback a ID interno
    const filter: any = {};
    if (plan.paymentLinkWompiId) {
      filter.paymentLinkId = plan.paymentLinkWompiId;
    } else if (plan.paymentLinkId) {
      filter.paymentLinkIdInternal = plan.paymentLinkId;
    }

    this.wompiWebhookService.getRequests(filter).subscribe({
      next: (webhooks) => {
        this.webhooksByPlan[plan.id] = webhooks;
        this.webhooksLoadingByPlan[plan.id] = false;
      },
      error: (err) => {
        console.error('Error al cargar webhooks para plan', plan.id, err);
        this.webhooksByPlan[plan.id] = [];
        this.webhooksLoadingByPlan[plan.id] = false;
      }
    });
  }

  /**
   * Obtiene los webhooks cargados para un plan
   */
  getWebhooksForPlan(plan: any): WompiWebhookRequest[] {
    return this.webhooksByPlan[plan.id] || [];
  }

  /**
   * Verifica si un plan tiene webhooks cargados
   */
  hasWebhooks(plan: any): boolean {
    return this.webhooksByPlan[plan.id] !== undefined && this.webhooksByPlan[plan.id].length > 0;
  }

  /**
   * Verifica si los webhooks están cargando para un plan
   */
  isWebhooksLoading(plan: any): boolean {
    return this.webhooksLoadingByPlan[plan.id] === true;
  }

  /**
   * Expande/colapsa el detalle de un webhook y carga los datos completos
   */
  toggleWebhookDetail(webhook: WompiWebhookRequest): void {
    const id = webhook.id;
    this.expandedWebhooks[id] = !this.expandedWebhooks[id];

    // Cargar detalle si se expande y no se ha cargado
    if (this.expandedWebhooks[id] && !this.webhookDetail[id]) {
      this.webhookDetailLoading[id] = true;
      this.wompiWebhookService.getRequestById(id).subscribe({
        next: (detail) => {
          this.webhookDetail[id] = detail;
          this.webhookDetailLoading[id] = false;
        },
        error: (err) => {
          console.error('Error al cargar detalle del webhook', id, err);
          this.webhookDetailLoading[id] = false;
        }
      });
    }
  }

  /**
   * Verifica si un webhook está expandido
   */
  isWebhookExpanded(webhookId: number): boolean {
    return this.expandedWebhooks[webhookId] === true;
  }

  /**
   * Obtiene el nombre del estado de procesamiento del webhook
   */
  getWebhookStatusLabel(status: any): string {
    return this.wompiWebhookService.getStatusName(status);
  }

  /**
   * Obtiene el color del estado de la transacción Wompi
   */
  getTransactionStatusColor(status: string | null | undefined): string {
    if (!status) return 'text-gray-500';
    switch (status.toUpperCase()) {
      case 'APPROVED': return 'text-emerald-600';
      case 'DECLINED': return 'text-red-600';
      case 'PENDING': return 'text-yellow-600';
      case 'VOIDED': return 'text-gray-600';
      case 'ERROR': return 'text-red-600';
      default: return 'text-gray-500';
    }
  }

  /**
   * Obtiene la clase CSS para el badge de estado de procesamiento
   */
  getWebhookProcessingBadgeClass(status: any): string {
    switch (status) {
      case 0: // Received
      case 'Received':
        return 'bg-blue-100 text-blue-700';
      case 1: // Processing
      case 'Processing':
        return 'bg-yellow-100 text-yellow-700';
      case 2: // Success
      case 'Success':
        return 'bg-emerald-100 text-emerald-700';
      case 3: // Failed
      case 'Failed':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  }

  /**
   * Formatea monto en centavos a moneda legible
   */
  formatWebhookAmount(amountInCents: number, currency?: string | null): string {
    return this.wompiWebhookService.formatAmount(amountInCents, currency || 'COP');
  }

  /**
   * Parsea los logs de procesamiento del webhook
   */
  parseProcessingLogs(logsJson: string | null | undefined): ProcessingLogEntry[] {
    if (!logsJson) return [];
    try {
      return JSON.parse(logsJson);
    } catch {
      return [];
    }
  }

  /**
   * Obtiene la clase CSS para el nivel de log
   */
  getLogLevelClass(level: string): string {
    switch (level) {
      case 'ERR': return 'text-red-600';
      case 'WRN': return 'text-yellow-600';
      case 'INF': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  }

  /**
   * Abre el modal con el JSON crudo del webhook
   */
  openRawWebhookModal(rawData: string): void {
    try {
      this.rawWebhookData = JSON.stringify(JSON.parse(rawData), null, 2);
    } catch {
      this.rawWebhookData = rawData;
    }
    this.showRawWebhookModal = true;
  }

  /**
   * Cierra el modal del JSON crudo
   */
  closeRawWebhookModal(): void {
    this.showRawWebhookModal = false;
    this.rawWebhookData = '';
  }

}

